<template>
  <div class="" :style="{ padding: isMobileScreen ? '0' : '1.5rem' }">
    <!-- Prompt / Image Section -->
    <div :class="isMobileScreen ? 'p-1' : ''">
      <page-separator :class="isMobileScreen ? 'mt-3' : ''" title="PROMPT / IMAGE" />

      <div v-if="!isMobileScreen" class="d-flex justify-content-end mb-3">
        <b-btn
          v-if="regenerateImage"
          variant="primary"
          class="mr-2"
          :disabled="isImageGenerating"
          @click.prevent="generateAISkillImage(true)"
        >
          <md-icon class="mr-1">autorenew</md-icon>
          REGENERATE
        </b-btn>
        <b-btn variant="secondary" @click.prevent="copyContent()">
          <md-icon v-if="isCopied" class="mr-1">check</md-icon>
          <md-icon v-if="!isCopied" class="mr-1">content_copy</md-icon>
          COPY
        </b-btn>
      </div>
    </div>

    <div v-if="isMobileScreen" class="mb-3" style="width:-webkit-fill-available;">
      <b-btn
        v-if="regenerateImage"
        variant="primary"
        class="mb-2"
        style="width: inherit;"
        :disabled="isImageGenerating"
        @click.prevent="generateAISkillImage(true)"
      >
        <md-icon class="mr-1">autorenew</md-icon>
        REGENERATE
      </b-btn>
      <b-btn variant="secondary" style="width: inherit;" @click.prevent="copyContent()">
        <md-icon class="mr-1">content_copy</md-icon>
        COPY
      </b-btn>
    </div>
    <div>
      <div class="form-group">
        <textarea readonly class="form-control" rows="10" v-model="prompt"></textarea>
      </div>
      <div :class="isMobileScreen ? 'p-1' : ''" v-if="showResults">
        <page-separator :class="isMobileScreen ? 'mt-3' : ''" title="RESULTS" />

        <div class="flex mb-3">
          <div class="mb-3" v-if="results.length">
            <div class="flex mb-2">
              <div v-if="precision" style="display: flex; justify-content: center;">
                <div class="circular-progress">
                  <div class="progress-container">
                    <svg class="progress-ring" width="200" height="200">
                      <!-- Background circle -->
                      <circle
                        class="progress-ring-background"
                        cx="100"
                        cy="100"
                        :r="radius"
                        fill="transparent"
                        stroke="#e2e8f0"
                        stroke-width="8"
                      />

                      <!-- Progress circle -->
                      <circle
                        class="progress-ring-progress"
                        cx="100"
                        cy="100"
                        :r="radius"
                        fill="transparent"
                        :stroke="progressColor()"
                        stroke-width="8"
                        stroke-linecap="round"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="strokeDashoffset()"
                        transform="rotate(-90 100 100)"
                      />
                    </svg>

                    <div class="progress-content">
                      <div class="percentage-text" :style="{ color: progressColor() }">{{ precision }}%</div>
                      <div class="label-text">{{ 'Precision' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-for="result in results" :key="result.anchor">
              <div
                class="result-anchor mb-2 d-flex align-items-start"
                :style="{ color: result.is_correct ? 'black' : 'red' }"
              >
                <i class="material-icons icon-18pt mr-1  flex-shrink-0" style="color: green;" v-if="result.is_correct"
                  >check_circle</i
                >
                <i class="material-icons icon-18pt mr-1  flex-shrink-0" v-else>cancel</i>

                <span style="flex: 1;">
                  {{ result.anchor }}
                </span>
              </div>
            </div>
            <div class="text-muted mt-3">
              Note: Your ai skill card will be published once it is approved by the admin
            </div>
          </div>
        </div>
      </div>

      <!-- Visualization Preview -->
      <page-separator title="VISUALIZATION PREVIEW" />
      <div class="card" :style="{ height: isMobileScreen ? '300px' : '' }">
        <img v-if="imageUrl && !isLoaded" :src="imageUrl" @load="onImageLoad" style="display: none;" />
        <!-- successfully generated image -->
        <div class="preview-placeholder" v-if="isLoaded">
          <b-img
            :src="imageUrl"
            alt="Generated image"
            :style="{ height: isMobileScreen ? '15rem' : '30rem' }"
            :class="['object-contain', { 'fade-in': shouldFadeIn }]"
          />
        </div>

        <!-- image is generating -->
        <div class="preview-placeholder" v-if="isImageGenerating">
          <div class="image-loader" :style="{ height: isMobileScreen ? '15rem' : '30rem' }">
            <div class="loading-container">
              <i class="fas fa-circle-notch fa-spin mb-3" style="font-size: 30px; color: aliceblue;"></i>
              <div class="progress-bar">
                <div class="progress" :style="{ width: progress + '%' }"></div>
              </div>
              <div class="progress-text">{{ loadText }}</div>
            </div>
            <img
              class="placeholder-image"
              :src="loaderImage"
              alt="Placeholder Image"
              :class="{ 'fade-out': shouldFadeOut }"
            />
          </div>
        </div>
        <!-- image creation failed -->
        <div v-if="!isImageGenerating && emptySkillCard" class="image-fail-card text-muted">
          <div
            class="flex text-center"
            :style="{
              height: isMobileScreen ? '15rem' : '30rem',
              fontSize: isMobileScreen ? '12px' : '',
              padding: isMobileScreen ? '10px' : '',
              alignContent: 'center',
            }"
          >
            <md-icon :style="{ fontSize: isMobileScreen ? '30px' : '70px' }">warning</md-icon>
            <p class="mb-1" style="font-size: x-large; font-weight: bold;">Generation Failed</p>
            <p>Failed to generate image. Please try again later.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { SKILLCARD_STATUS } from '@/common/constants';

export default {
  name: 'PromptImageForm',
  components: { PageSeparator },
  props: {
    activeTabIndex: Number,
    myIndex: Number,
  },
  data() {
    return {
      prompt: '',
      imageUrl: null,
      windowWidth: window.innerWidth,
      isCopied: false,
      isActive: false,
      connection: null,
      skillCardId: null,
      regenerateImage: false,
      emptySkillCard: false,
      isImageGenerating: false,
      SKILLCARD_STATUS,
      placeholders: [
        require('@/assets/images/placeholders/load1.jpg'),
        require('@/assets/images/placeholders/load2.jpg'),
      ],
      loadingText: ['Analyzing prompt...', 'Generating scene...', 'Adding details...', 'Finalyzing image...'],
      loadText: 'Analyzing prompt...',
      progress: 0,
      isLoaded: false,
      loaderImage: null,
      loadingInterval: null,
      shouldFadeOut: false,
      shouldFadeIn: false,
      results: [],
      precision: '',
      status: '',
      radius: 60,
      showResults: false,
      STATUSES: { pending: 'Pending', approved: 'Not Approved', rejected: 'Rejected' },
    };
  },
  computed: {
    ...mapGetters('aiSkills', ['getAiSkillPrompt', 'getSkillCard', 'getRequirePatient']),
    ...mapGetters('auth', ['getLoggedInUser', 'getAuthToken']),

    isMobileScreen() {
      return this.windowWidth <= 420;
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
  },
  watch: {
    activeTabIndex: {
      immediate: true,
      handler() {
        this.isActive = this.activeTabIndex === this.myIndex;
        if (this.isActive) {
          this.generateAISkillImage();
        } else {
          this.closeConnection();
        }
      },
    },
    skillCardId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.hookUrlWS(newVal);
        }
      },
    },
  },
  methods: {
    ...mapActions('aiSkills', ['getSkillCardPrompt', 'getSkillCardImage']),

    strokeDashoffset() {
      const precision = this.precision ? this.precision : '0';
      return this.circumference - (precision / 100) * this.circumference;
    },
    progressColor() {
      if (this.precision < 50) {
        return '#ef4444'; // Red
      } else if (this.precision < 70) {
        return '#f59e0b'; // Yellow/Amber
      } else {
        return '#10b981'; // Green
      }
    },
    async generateAISkillImage() {
      this.isImageGenerating = true;
      if (!this.getSkillCard.step || !this.getSkillCard.skill_map) {
        this.emptySkillCard = true;
        this.isImageGenerating = false;
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields on previous form.' });
        return;
      }
      this.emptySkillCard = false;

      if (this.regenerateImage) {
        this.imageUrl = null;
        this.showResults = false;
      }
      this.simulateLoading();
      this.isLoaded = false;
      const data = {
        step: this.getSkillCard.step,
        skill_map: this.getSkillCard.skill_map,
        competition: this.getSkillCard.competition,
        actor_race: this.getSkillCard.actor_race,
        actor_gender: this.getSkillCard.actor_gender,
        actor_age: this.getSkillCard.actor_age,
        uniform_type: this.getSkillCard.uniform_type,
        uniform_color: this.getSkillCard.uniform_color,
        ...(this.getRequirePatient
          ? {
              patient_age: this.getSkillCard.patient_age,
              patient_condition: this.getSkillCard.patient_condition,
              patient_position: this.getSkillCard.patient_position,
              patient_race: this.getSkillCard.patient_race,
              patient_gender: this.getSkillCard.patient_gender,
            }
          : {}),
        environment_setting: this.getSkillCard.environment_setting,
        room_type: this.getSkillCard.room_type,
        time_of_day: this.getSkillCard.time_of_day,
        scene_mood: this.getSkillCard.scene_mood,
        anchors: this.getSkillCard.anchorElements,
      };
      const promptResp = await this.getSkillCardPrompt(data);
      this.prompt = promptResp.data.prompt;
      const resp = await this.getSkillCardImage(data);
      this.skillCardId = resp.data.skill_card_id;
      this.results = resp.data.result ? resp.data.result : [];
      this.precision = resp.data.precision;
      this.status = resp.data.status;
    },
    simulateLoading() {
      this.progress = 0;
      this.loaderImage = this.placeholders[0];
      const interval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += 10;
          this.loaderImage = this.progress >= 50 ? this.placeholders[1] : this.placeholders[0];
          this.loadText = this.loadingText[Math.min(3, Math.floor(this.progress / 25))];
        }
      }, 7000);
      this.loadingInterval = interval;
    },
    onImageLoad() {
      this.progress = 100;
      this.shouldFadeOut = true;
      setTimeout(() => {
        this.isLoaded = true;
        this.isImageGenerating = false;
        this.shouldFadeIn = true;
        this.showResults = true;
      }, 1000);
      clearInterval(this.loadingInterval);
      this.makeToast({ variant: 'success', msg: 'Your image is ready!' });
    },
    hookUrlWS(skillcardId) {
      this.connection = new WebSocket(
        `${process.env.VUE_APP_BACKEND_WS_URL}/ws/ai/skillcard/${skillcardId}/?token=${this.getAuthToken}`,
      );

      this.connection.onmessage = event => {
        const data = JSON.parse(event.data).message;

        if (data.action === 'Create') {
          this.regenerateImage = false;
          this.emptySkillCard = false;
          this.imageUrl = data.image_url;
        }
        if (data.action === 'Creation_Failed') {
          this.regenerateImage = true;
          this.emptySkillCard = true;
          this.isImageGenerating = false;
          this.imageUrl = null;
          this.makeToast({ variant: 'danger', msg: 'Image generation failed. Please try again!' });
        }
      };
      this.connection.onerror = () => {
        this.makeToast({ variant: 'danger', msg: 'Error in generating image!' });
      };
    },
    closeConnection() {
      if (this.connection) this.connection.close();
    },
    async copyContent() {
      try {
        await navigator.clipboard.writeText(this.prompt);
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 10000);
        this.makeToast({ variant: 'success', msg: 'Prompt copied!' });
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: 'Failed to copy!' });
      }
    },
  },
  async mounted() {},
};
</script>

<style scoped>
.container {
  padding: 1.5rem;
  min-height: 100vh;
}

.card {
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
}

.form-label {
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  resize: vertical;
}
.result-anchor {
  border: solid 1px #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  background-color: #f3f5f8;
}

.anchor-elements {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #374151;
}

.anchor-elements ul {
  list-style-type: disc;
  padding-left: 1.25rem;
}

.preview-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30rem;
  border: 2px dashed #d1d5db;
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.875rem;
}
.image-fail-card {
  font-size: 1.25rem;
  border-radius: 0.375rem;
}

.image-loader {
  position: relative;
  width: 600px;
  background-color: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  z-index: 2;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.progress-text {
  color: white;
  margin-top: 10px;
  font-size: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.placeholder-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.5s ease;
}

.circular-progress {
  display: inline-block;
  position: relative;
}

.progress-container {
  position: relative;
  display: inline-block;
}

.progress-ring-background {
  opacity: 0.5;
}

.progress-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.percentage-text {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.label-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.fade-out {
  animation: fadeOut 1s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 1s ease-in forwards;
}
</style>
